import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { api } from '../api/client';
import type { Signal } from '../types';
import Code from '../components/Code';
import { useObjectiveName } from '../objectives';

/**
 * Smart /drift index route.
 * - If 0 open drift packets: show helpful empty state + suggested signals to inject.
 * - If exactly 1: auto-navigate to its detail page.
 * - If multiple: show a selector list, click goes to detail.
 */
export default function DriftList() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const driftList = useQuery({ queryKey: ['drift', 'list'], queryFn: api.driftList });
  const signals = useQuery({ queryKey: ['signals'], queryFn: api.signals });
  const objName = useObjectiveName();

  const inject = useMutation({
    mutationFn: (id: string) => api.injectSignal(id, 1.0),
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: ['signals'] });
      await qc.invalidateQueries({ queryKey: ['drift', 'list'] });
      if (data.drift_packet_id) navigate(`/drift/${data.drift_packet_id}`);
    },
  });

  // Auto-redirect when exactly one packet exists
  useEffect(() => {
    if (driftList.data && driftList.data.length === 1) {
      navigate(`/drift/${driftList.data[0].id}`, { replace: true });
    }
  }, [driftList.data, navigate]);

  if (driftList.isLoading) return <div className="small faint">Loading drift packets…</div>;

  const packets = driftList.data ?? [];
  const suggestedSignals: Signal[] = (signals.data ?? [])
    .filter((s) => !s.tripwire_breached)
    .slice(0, 4);

  // Empty state — no open packets
  if (packets.length === 0) {
    return (
      <div>
        <div className="ph">Strategy Drift Packet</div>
        <div className="h2">No active drift packets</div>

        <div className="panel">
          <div className="qhead">
            <span className="qdot" style={{ background: 'var(--amber)' }} />
            Inject a signal to fire a drift packet
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
            {suggestedSignals.map((s) => (
              <div
                key={s.id}
                style={{
                  padding: '14px 16px',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  background: 'var(--panel2)',
                }}
              >
                <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
                  <span className="tag amber"><Code id={s.id} kind="signal" compact /></span>
                  <span className="small" style={{ color: 'var(--faint)' }}>
                    <Code id={s.assumption_id} kind="assumption" compact />{objName(s.objective_id) ? <> · {objName(s.objective_id)}</> : null} · <Code id={s.money_node_id} kind="stage" compact />
                  </span>
                </div>
                <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500, marginBottom: 4 }}>{s.name}</div>
                <div className="small" style={{ marginBottom: 12 }}>{s.owner}</div>
                <button
                  type="button"
                  className="btn primary"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => inject.mutate(s.id)}
                  disabled={inject.isPending}
                >
                  {inject.isPending ? 'Firing…' : '⚡ Fire signal'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // List view — multiple packets
  return (
    <div>
      <div className="ph">Strategy Drift Packets · open queue</div>
      <div className="h2">{packets.length} open packet{packets.length === 1 ? '' : 's'}</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 12 }}>
        {packets.map((p) => (
          <Link key={p.id} to={`/drift/${p.id}`} style={{ textDecoration: 'none' }}>
            <div className="driftcard" style={{ height: '100%' }}>
              <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
                <span className="tag red">OPEN</span>
                <span className="small faint">{relativeTime(p.ts)}</span>
              </div>
              <div className="cond" style={{ fontSize: 17, marginBottom: 8, color: 'var(--ink)' }}>{p.signal_name}</div>
              <div className="small" style={{ marginBottom: 10 }}>
                Breaches <b style={{ color: 'var(--red)' }}><Code id={p.assumption_id} kind="assumption" compact /></b>{' '}
                {objName(p.objective_id) ? <>· threatens <span className="tag amber">{objName(p.objective_id)}</span>{' '}</> : null}
                · node <span className="tag teal"><Code id={p.money_node_id} kind="stage" compact /></span>
              </div>
              <div className="small">Owner: <b>{p.owner}</b></div>
              {p.economic_consequence && (
                <div className="small" style={{ marginTop: 8, color: 'var(--muted)', lineHeight: 1.4 }}>
                  {p.economic_consequence.slice(0, 110)}{p.economic_consequence.length > 110 ? '…' : ''}
                </div>
              )}
              <div className="small" style={{ marginTop: 10, color: 'var(--amber)' }}>open packet →</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
